// @ts-strict-ignore
import {DisplayBookmark, TimeFilter} from 'common/report/dashboards/types';
import {parseJsUrl, serializeJsUrl} from 'common/report/util/url';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {RCASourceData} from 'common/report/queries/app-api/dashboards-client';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {isValidBookmarkURLSlug} from 'common/report/util/bookmark-urls';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {SearchDisplayContext, SearchSortOption} from 'common/report/util/search/types';
import {BROWSE_URL_PARAM, BrowseEntityTypeFilter} from 'common/report/util/search';
import {BoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {Segmentation} from 'common/types/reports/query';

export type BrowseUrlData = {
  isBrowseMode: true;
  emailFilter?: string;
  searchText?: string;
  entityTypeFilter?: BrowseEntityTypeFilter;
  eventNameFilter?: string;
  sort?: SearchSortOption;
};

export type DashboardViewUrlData = {
  dashboardId?: number;
  timeFilter?: null | TimeFilter;
  filters?: BoardFilter[];
  breakdowns?: Segmentation[];
  tvModeEnabled?: boolean;
  isOnboardingDashboard?: boolean;
  focusedBookmarkId?: number;
  focusedCardId?: string | number;
  rcaSourceId?: number;
  rcaSourceData?: RCASourceData;

  // fast-edit params
  editorCardId?: DashboardCardId;
  editorCardEditedBookmark?: DisplayBookmark;
  editorCardBookmarkURLSlug?: string;
};

export type DashboardUrlData = DashboardViewUrlData | BrowseUrlData;

export function isBrowseUrlData(urlData: DashboardUrlData): urlData is BrowseUrlData {
  return `isBrowseMode` in urlData;
}

interface DashboardUrlHashParamsParseResult {
  correctedHashParams: string;
  parsedData: DashboardUrlData;
}

export function serializeDashboardUrlHashParams(urlData: DashboardUrlData): string {
  const params = new URLSearchParams();
  if (isBrowseUrlData(urlData)) {
    return params.toString();
  }

  if (Number.isInteger(urlData.dashboardId)) {
    params.append(`id`, urlData.dashboardId.toString());
  }

  if (`timeFilter` in urlData) {
    if (urlData.timeFilter) {
      const jsUrlEncoded = serializeJsUrl(urlData.timeFilter);
      params.append(`timeFilter`, jsUrlEncoded);
    } else {
      params.append(`timeFilter`, ``);
    }
  }

  if (`filters` in urlData) {
    if (urlData.filters && urlData.filters.length > 0) {
      const jsUrlEncoded = serializeJsUrl(urlData.filters);
      params.append(`filters`, jsUrlEncoded);
    } else {
      params.append(`filters`, ``);
    }
  }

  if (`breakdowns` in urlData && urlData.breakdowns && urlData.breakdowns.length > 0) {
    const jsUrlEncoded = serializeJsUrl(urlData.breakdowns);
    params.append(`breakdowns`, jsUrlEncoded);
  }

  if (urlData.tvModeEnabled) {
    params.append(`tv`, JSON.stringify(true));
  }

  if (urlData.focusedBookmarkId) {
    params.append(`focused-bookmark-id`, urlData.focusedBookmarkId.toString());
  }

  if (urlData.focusedCardId) {
    params.append(`card-id`, urlData.focusedCardId.toString());
  }

  if (urlData.editorCardId) {
    params.append(`editor-card-id`, JSON.stringify(urlData.editorCardId));
  }

  if (urlData.editorCardBookmarkURLSlug) {
    params.append(`edited-bookmark`, urlData.editorCardBookmarkURLSlug);
  } else if (urlData.editorCardEditedBookmark) {
    params.append(`edited-bookmark`, serializeJsUrl(urlData.editorCardEditedBookmark));
  }

  return params.toString();
}

export const STAGED_CARD_PREFIX = `new-`;

export function generateStagedCardId(type: BookmarkType): string {
  return `${STAGED_CARD_PREFIX}${type}`;
}

export function isStagedCardId(editorCardId?: DashboardCardId): boolean {
  return Boolean(editorCardId?.toString().startsWith(STAGED_CARD_PREFIX));
}

function parseBrowseUrlHashParams(params: URLSearchParams): DashboardUrlHashParamsParseResult {
  const searchText = params.get(`q`);
  const emailFilter = params.get(`email`);
  const entityTypeFilter = params.get(`entity-type-filter`) as SearchDisplayContext;
  const eventNameFilter = params.get(`event-name-filter`);
  const sort = params.get(`sort`) as SearchSortOption;
  return {
    parsedData: {
      isBrowseMode: true,
      searchText,
      emailFilter,
      entityTypeFilter,
      eventNameFilter,
      sort,
    },
    correctedHashParams: BROWSE_URL_PARAM,
  };
}

// TODO opslead
export function parseDashboardUrlHashParams(hashParams: string): DashboardUrlHashParamsParseResult {
  const params = new URLSearchParams(hashParams);
  const parsedData: DashboardUrlData = {};

  const isBrowseMode = params.has(BROWSE_URL_PARAM);

  // dashboard ID
  const dashboardId = parseInt(params.get(`id`), 10);
  if (Number.isInteger(dashboardId)) {
    parsedData.dashboardId = dashboardId;
  } else if (isBrowseMode) {
    // skip the rest of dashboard parsing for browse mode
    // TODO: consider decoupling browse from standard-mode (potentially expensive refactor)
    return parseBrowseUrlHashParams(params);
  } else if (params.has(`rca`)) {
    const sourceData = {};
    parsedData.rcaSourceId = parseInt(params.get(`rca`), 10);

    if (params.has(`time`)) {
      sourceData[`timestamp`] = params.get(`time`);
    }
    if (params.has(`type`)) {
      sourceData[`type`] = params.get(`type`);
    } else {
      sourceData[`type`] = `alert`;
    }
    if (params.has(`date`)) {
      sourceData[`date`] = params.get(`date`);
    }
    if (params.has(`property`)) {
      sourceData[`property`] = params.get(`property`);
    }
    if (params.has(`metric_source`)) {
      sourceData[`metric_source`] = JSON.parse(params.get(`metric_source`));
    } else {
      sourceData[`metric_source`] = false;
    }
    parsedData.rcaSourceData = sourceData;
  }
  // global filters
  if (params.has(`timeFilter`)) {
    const jsUrlInfo = parseJsUrl(params.get(`timeFilter`));
    if (jsUrlInfo.valid) {
      parsedData.timeFilter = jsUrlInfo.data;
      if (`dateRange` in parsedData.timeFilter) {
        const dateRange = new DateRange(parsedData.timeFilter.dateRange);
        parsedData.timeFilter.dateRange = dateRange;
      }
      params.set(`timeFilter`, jsUrlInfo.correctedUrl); // repair malformed JSURL
    } else if (params.get(`timeFilter`) === ``) {
      params.set(`timeFilter`, ``);
      parsedData.timeFilter = null;
    } else {
      console.error(`Invalid JSURL-encoded timeFilter`, params.get(`timeFilter`));
    }
  }
  if (params.has(`filters`)) {
    const jsUrlInfo = parseJsUrl(params.get(`filters`));
    if (jsUrlInfo.valid) {
      if (Array.isArray(jsUrlInfo.data)) {
        parsedData.filters = jsUrlInfo.data;
        params.set(`filters`, jsUrlInfo.correctedUrl); // repair malformed JSURL
      } else if (typeof jsUrlInfo.data === `object`) {
        // if the "filters" params is an object, we know it's the legacy hash params format
        /* ------- start legacy format parsing ------- */
        if (jsUrlInfo.data.timeFilter) {
          parsedData.timeFilter = {
            displayText: jsUrlInfo.data.timeFilter.displayText,
            dateRange: jsUrlInfo.data.timeFilter.selectedFilterObject,
          };
          params.set(`timeFilter`, serializeJsUrl(parsedData.timeFilter));
        }

        if (jsUrlInfo.data.filters) {
          parsedData.filters = jsUrlInfo.data.filters;
          params.set(`filters`, serializeJsUrl(parsedData.filters));
        } else {
          parsedData.filters = [];
          // if legacy format has no property filters, the "filters" hash param should not exist
          params.delete(`filters`);
        }
        /* ------- end legacy format parsing ------- */
      }
    } else if (params.get(`filters`) === ``) {
      params.set(`filters`, ``);
      parsedData.filters = null;
    } else {
      console.error(`Invalid JSURL-encoded filters`, params.get(`filters`));
    }
  }
  if (params.has(`breakdowns`)) {
    const jsUrlInfo = parseJsUrl(params.get(`breakdowns`));
    if (jsUrlInfo.valid) {
      parsedData.breakdowns = jsUrlInfo.data;
      params.set(`breakdowns`, jsUrlInfo.correctedUrl); // repair malformed JSURL
    } else if (params.get(`breakdowns`) === ``) {
      params.set(`breakdowns`, ``);
      parsedData.breakdowns = null;
    } else {
      console.error(`Invalid JSURL-encoded breakdowns`, params.get(`breakdowns`));
    }
  }

  if (params.has(`tv`)) {
    parsedData.tvModeEnabled = JSON.parse(params.get(`tv`)) as boolean;
  } else {
    parsedData.tvModeEnabled = false;
  }

  if (params.has(`focused-bookmark-id`)) {
    const focusedBookmarkId = parseInt(params.get(`focused-bookmark-id`));
    if (focusedBookmarkId) {
      parsedData.focusedBookmarkId = focusedBookmarkId;
    }
  }

  if (params.has(`card-id`)) {
    const focusedCardId = params.get(`card-id`);
    if (focusedCardId) {
      parsedData.focusedCardId = focusedCardId;
    }
  }

  let editorCardEditedBookmark: DisplayBookmark;
  if (params.has(`edited-bookmark`)) {
    const jsurlOrBookmarkURLSlug = params.get(`edited-bookmark`);
    if (isValidBookmarkURLSlug(jsurlOrBookmarkURLSlug)) {
      parsedData.editorCardBookmarkURLSlug = jsurlOrBookmarkURLSlug;
    } else if (parseJsUrl(jsurlOrBookmarkURLSlug).valid) {
      parsedData.editorCardEditedBookmark = parseJsUrl(jsurlOrBookmarkURLSlug).data;
    }
    editorCardEditedBookmark = parsedData.editorCardEditedBookmark;
  }

  if (params.has(`editor-card-id`)) {
    let editorCardId = JSON.parse(params.get(`editor-card-id`));
    if (editorCardId === `-1` && editorCardEditedBookmark) {
      // Convert old URL format to new format.
      editorCardId = generateStagedCardId(editorCardEditedBookmark.type);
    }
    parsedData.editorCardId = editorCardId;
  }

  return {parsedData, correctedHashParams: params.toString()};
}
